import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import { Observable, map } from 'rxjs';
import { LocalStorageService } from '@/app/core/storage/local-storage.service';
import { SessionStorageService } from '@/app/core/storage/session-storage.service';
import {
  UserProxy,
  SigninUserCommand,
  SigninUserResponseResponseResult,
  UserProfileViewModel,
  ErrorMessage
} from '@/app/proxy/proxies';

export interface UserSession {
  id: string;
  token: string;
  validUntil: string;
  name?: string;
  email?: string;
}

export interface LoginResponse {
  isSuccess: boolean;
  errors?: ErrorMessage[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  // Dependencies
  private readonly userProxy = inject(UserProxy);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly router = inject(Router);

  // Signal-based state
  private readonly _currentSession = signal<UserSession | null>(null);
  private readonly _isLoading = signal<boolean>(false);
  private readonly _userProfile = signal<UserProfileViewModel | null>(null);

  // Computed signals
  readonly currentSession = this._currentSession.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly isLoggedIn = computed(() => !!this._currentSession() && this.isTokenValid());
  readonly userProfile = this._userProfile.asReadonly();

  // Computed user initials for avatar
  readonly userInitials = computed(() => {
    const profile = this._userProfile();
    if (profile) {
      const firstInitial = profile.name?.charAt(0)?.toUpperCase() || '';
      const lastInitial = profile.surname?.charAt(0)?.toUpperCase() || '';
      return firstInitial + lastInitial || 'U';
    }
    return 'U';
  });

  constructor() {
    // Initialize session from storage on service creation
    this.initializeSession();
  }

  private initializeSession(): void {
    const token = this.localStorageService.getItem<string>('token');
    const tokenExpiration = this.localStorageService.getItem<string>('tokenExpiration');
    const userId = this.localStorageService.getItem<string>('userId');

    if (token && tokenExpiration && userId && this.isTokenValid()) {
      this._currentSession.set({
        id: userId,
        token: token,
        validUntil: tokenExpiration,
      });
      // Fetch user profile on session restore
      this.fetchUserProfile(userId);
    }
  }

  fetchUserProfile(userId: string): void {
    this.userProxy.user_GetUserProfile(userId).subscribe({
      next: (response) => {
        if (response.isSuccess && response.result) {
          this._userProfile.set(response.result);
        }
      },
      error: (err) => console.error('Failed to fetch user profile:', err)
    });
  }

  isAuthenticated(): boolean {
    return this.isTokenValid();
  }

  private isTokenValid(): boolean {
    const token = this.localStorageService.getItem<string>('token');
    const tokenExpiration = this.localStorageService.getItem<string>('tokenExpiration');

    if (!token || !tokenExpiration) {
      return false;
    }

    const expirationDate = DateTime.fromISO(tokenExpiration);
    return expirationDate > DateTime.now();
  }

  /**
   * Login with phone number and password
   */
  login(phoneCode: string, phoneNumber: string, password: string): Observable<LoginResponse> {
    // Clear any existing token to avoid sending stale credentials in headers
    this.localStorageService.removeItem('token');
    this.localStorageService.removeItem('userId');
    this.localStorageService.removeItem('tokenExpiration');
    this._currentSession.set(null);

    const command = new SigninUserCommand({
      email: undefined,
      password: password,
      phone: phoneNumber,
      phoneCode: phoneCode,
      newPassword: undefined,
      externalProviderType: undefined,
      externalProviderToken: undefined,
      captchaToken: undefined,
    });

    return this.userProxy.user_Signin(command).pipe(
      map((response: SigninUserResponseResponseResult) => {
        if (response.isSuccess && response.result) {
          this.handleSigninResponse({
            id: response.result.id,
            token: response.result.token,
            validUntil: response.result.validUntil?.toString(),
            name: undefined,
            email: undefined,
          });
          return {
            isSuccess: true,
          } as LoginResponse;
        }
        // Return false on failure - ApiToastInterceptor already shows the error toast
        return {
          isSuccess: false,
          errors: response.errors as ErrorMessage[],
        } as LoginResponse;
      })
    );
  }

  handleSigninResponse(response: {
    id: string;
    token: string;
    validUntil?: string;
    name?: string;
    email?: string;
  }): void {
    // Store in local storage
    this.localStorageService.setItem('userId', response.id);
    this.localStorageService.setItem('token', response.token);
    this.localStorageService.setItem('tokenExpiration', response.validUntil ?? '');

    // Update session state
    this._currentSession.set({
      id: response.id,
      token: response.token,
      validUntil: response.validUntil ?? '',
      name: response.name,
      email: response.email,
    });

    // Fetch user profile to get name/surname for initials
    this.fetchUserProfile(response.id);
  }

  logout(): void {
    // Blur any focused element to avoid masked inputs receiving late input events (Android InputMask bug)
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    // Preserve language settings
    const languageGuid = this.localStorageService.getItem<string>('languageGuid');
    const languageISO1 = this.localStorageService.getItem<string>('languageISO1');

    // Clear storage
    this.localStorageService.clear();
    this.sessionStorageService.clear();

    // Restore language settings
    if (languageGuid) {
      this.localStorageService.setItem('languageGuid', languageGuid);
    }
    if (languageISO1) {
      this.localStorageService.setItem('languageISO1', languageISO1);
    }

    // Clear session state
    this._currentSession.set(null);

    // Navigate to login (defer slightly to let pending input events settle, esp. on Android)
    setTimeout(() => {
      this.router.navigate(['/auth/login']);
    }, 0);
  }

  getToken(): string | null {
    return this.localStorageService.getItem<string>('token');
  }

  getUserId(): string | null {
    return this.localStorageService.getItem<string>('userId');
  }

  setLoading(loading: boolean): void {
    this._isLoading.set(loading);
  }
}



